import { useTranslation } from '@thoughtspot/i18n';
import {
    Alert,
    BannerAlertType,
} from '@thoughtspot/radiant-react/widgets/alert';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout/flex-layout/flex-layout';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import {
    Colors,
    Typography,
} from '@thoughtspot/radiant-react/widgets/typography';
import React, { useEffect, useState } from 'react';
import { useBoolean } from 'react-use';
import {
    UpdateDisplayNameDialogOrigin,
    UpdateDisplayNameProps,
} from '../invite-users.interface';
import styles from './update-display-name.module.scss';

export const UpdateDisplayNameDialog = ({
    updateDisplayName,
    updateDisplayNameError,
    isModalOpen,
    isUpdateInProgress,
    onDismissCB,
    originalDisplayName,
    origin,
}: UpdateDisplayNameProps) => {
    const { t } = useTranslation();
    const [isAlertBannerVisible, setIsAlertBannerVisible] = useBoolean(false);
    const [displayName, setDisplayName] = useState<string>(originalDisplayName);
    const [
        showEmptyDisplayNameError,
        setShowEmptyDisplayNameError,
    ] = useBoolean(false);

    const onSave = () => {
        if (displayName.trim() !== '') {
            updateDisplayName(displayName);
        } else {
            setShowEmptyDisplayNameError(true);
        }
    };
    const handleNameInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (showEmptyDisplayNameError) {
            setShowEmptyDisplayNameError(false);
        }
        setDisplayName(event.target.value);
    };

    useEffect(() => {
        if (updateDisplayNameError) {
            setIsAlertBannerVisible(true);
        }
    }, [updateDisplayNameError]);

    const description =
        origin === UpdateDisplayNameDialogOrigin.INVITE
            ? t('updateNameDialog.description.invite')
            : t('updateNameDialog.description.share');

    // doNotDismissOnOutsideClickOrEsc: we are adding this flag
    // as the workflow involves closing one dialog and opening another
    // also the main function that the user was trying to perform was share/invite
    return (
        <Modal
            isOpen={isModalOpen}
            dismissButton={{
                text: t('updateNameDialog.dismissBtn'),
                onClick: onDismissCB,
            }}
            confirmButton={{
                text: t('Save'),
                onConfirm: onSave,
            }}
            size={ModalSize.MEDIUM}
            title={t('updateNameDialog.header')}
            isContentLoading={isUpdateInProgress}
            doNotDismissOnOutsideClickOrEsc
            data-testid="update-display-name-dialog"
        >
            <Vertical className={styles.updateUserWrapper}>
                <Alert.Banner
                    isVisible={isAlertBannerVisible}
                    message={updateDisplayNameError?.message}
                    type={BannerAlertType.ERROR}
                    onClose={() => setIsAlertBannerVisible(false)}
                    className={styles.alertBanner}
                />
                <Typography variant="body-normal" noMargin>
                    {description}
                </Typography>
                <Typography variant="h5" className={styles.updateTitle}>
                    {t('updateNameDialog.inputHeader')}
                </Typography>
                <Input.Text
                    value={displayName}
                    onChange={handleNameInputChange}
                    placeholder={t('updateNameDialog.placeholder')}
                    ariaLabel={t('updateNameDialog.header')}
                    data-testid="display-name-edit"
                />
                {showEmptyDisplayNameError && (
                    <Typography variant="p" color={Colors.failure} noMargin>
                        {t('updateNameDialog.emptyError')}
                    </Typography>
                )}
            </Vertical>
        </Modal>
    );
};
